import React, { useEffect, useState } from 'react';
import { getProjects, getProject, createProject, updateProject, deleteProject, getProviders, getUsers, getCostCenters, addClientToProject, addAssignmentToProject, getFinancialLaunches } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import EmpreitasManager from '../components/EmpreitasManager';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FolderKanban, Users, Link2, Eye, Receipt, CheckCircle2, Clock } from 'lucide-react';

const Projects = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'administrator';
  const [projects, setProjects] = useState([]);
  const [providers, setProviders] = useState([]);
  const [users, setUsers] = useState([]);
  const [clientUsers, setClientUsers] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [servicesMap, setServicesMap] = useState({}); // Map service_code -> service details
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [activeTab, setActiveTab] = useState('dados');
  const [allLaunches, setAllLaunches] = useState([]); // All launches for calculating consumed values
  const [projectLaunches, setProjectLaunches] = useState([]); // Launches for current project
  
  // Form states
  const [projectData, setProjectData] = useState({
    name: '',
    internal_code: '',
    start_date: '',
    end_date: '',
    status: 'planned',
    address: '',
    notes: ''
  });
  
  const [clients, setClients] = useState([]); // Array of {user_id, name, email}
  
  const [assignments, setAssignments] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceDetails, setServiceDetails] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Client users only need to fetch projects and launches
      if (!isAdmin) {
        const [projectsRes, launchesRes] = await Promise.all([
          getProjects(),
          getFinancialLaunches()
        ]);
        setProjects(projectsRes.data);
        setAllLaunches(launchesRes.data);
        return;
      }
      
      const [projectsRes, providersRes, usersRes, costCentersRes, launchesRes] = await Promise.all([
        getProjects(),
        getProviders(),
        getUsers(),
        getCostCenters(),
        getFinancialLaunches()
      ]);
      setProjects(projectsRes.data);
      setProviders(providersRes.data);
      setUsers(usersRes.data);
      setAllLaunches(launchesRes.data);
      
      // Filter only client users
      const clients = usersRes.data.filter(u => u.role === 'client');
      setClientUsers(clients);
      
      setCostCenters(costCentersRes.data);
      
      // Build services map for quick lookup
      const map = {};
      costCentersRes.data.forEach(cc => {
        cc.services.forEach(service => {
          map[service.code] = {
            code: service.code,
            description: service.description,
            cost_center: cc.description
          };
        });
      });
      setServicesMap(map);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    }
  };

  const handleOpenDialog = (project = null, readOnly = false) => {
    setViewOnly(readOnly || !isAdmin);
    if (project) {
      setEditingProject(project);
      setProjectData({
        name: project.name,
        internal_code: project.internal_code || '',
        start_date: project.start_date || '',
        end_date: project.end_date || '',
        status: project.status,
        address: project.address || '',
        notes: project.notes || ''
      });
      setClients(project.clients || []);
      setAssignments(project.assignments || []);
      // Filter launches for this project
      const launches = allLaunches.filter(l => l.project_id === project.id);
      setProjectLaunches(launches);
    } else {
      resetForm();
      setProjectLaunches([]);
    }
    setIsDialogOpen(true);
    setActiveTab('dados');
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...projectData,
        clients,
        assignments: []  // assignments deprecated — provider linkage via Empreitas only
      };
      
      if (editingProject) {
        await updateProject(editingProject.id, payload);
        toast.success('Projeto atualizado com sucesso!');
      } else {
        await createProject(payload);
        toast.success('Projeto criado com sucesso!');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar projeto');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este projeto?')) {
      try {
        await deleteProject(id);
        toast.success('Projeto excluído com sucesso!');
        fetchData();
      } catch (error) {
        toast.error('Erro ao excluir projeto');
      }
    }
  };

  const resetForm = () => {
    setProjectData({
      name: '',
      internal_code: '',
      start_date: '',
      end_date: '',
      status: 'planned',
      address: '',
      notes: ''
    });
    setClients([]);
    setAssignments([]);
    setEditingProject(null);
    setSelectedProvider(null);
    setSelectedServices([]);
    setServiceDetails({});
  };

  // Client functions - Toggle client selection
  const toggleClientSelection = (clientUser) => {
    const isSelected = clients.some(c => c.user_id === clientUser.id);
    if (isSelected) {
      setClients(clients.filter(c => c.user_id !== clientUser.id));
    } else {
      setClients([...clients, {
        user_id: clientUser.id,
        name: clientUser.name,
        email: clientUser.email
      }]);
    }
  };

  const isClientSelected = (clientUserId) => {
    return clients.some(c => c.user_id === clientUserId);
  };

  // Assignment functions
  const handleProviderChange = (providerId) => {
    const provider = providers.find(p => p.id === providerId);
    setSelectedProvider(provider);
    setSelectedServices([]);
    setServiceDetails({});
  };

  const toggleService = (serviceCode) => {
    if (selectedServices.includes(serviceCode)) {
      setSelectedServices(selectedServices.filter(s => s !== serviceCode));
      const newDetails = { ...serviceDetails };
      delete newDetails[serviceCode];
      setServiceDetails(newDetails);
    } else {
      setSelectedServices([...selectedServices, serviceCode]);
      // Auto-fill description from services map
      const serviceInfo = servicesMap[serviceCode];
      if (serviceInfo) {
        setServiceDetails({
          ...serviceDetails,
          [serviceCode]: {
            description: serviceInfo.description,
            type: 'service_execution',
            value: '',
            notes: ''
          }
        });
      }
    }
  };

  const updateServiceDetails = (serviceCode, field, value) => {
    setServiceDetails({
      ...serviceDetails,
      [serviceCode]: {
        ...serviceDetails[serviceCode],
        [field]: value
      }
    });
  };

  const handleAddAssignment = () => {
    if (!selectedProvider) {
      toast.error('Selecione um prestador');
      return;
    }
    if (selectedServices.length === 0) {
      toast.error('Selecione pelo menos um serviço');
      return;
    }

    // Build services array with auto-filled descriptions
    const services = selectedServices.map(code => {
      const details = serviceDetails[code] || {};
      const serviceInfo = servicesMap[code];
      return {
        service_code: code,
        service_description: serviceInfo?.description || code,
        assignment_type: details.type || 'service_execution',
        planned_value: details.value ? parseFloat(details.value) : null,
        notes: details.notes || null
      };
    });

    const newAssignment = {
      provider_id: selectedProvider.id,
      provider_name: selectedProvider.name,
      services
    };

    setAssignments([...assignments, newAssignment]);
    setSelectedProvider(null);
    setSelectedServices([]);
    setServiceDetails({});
    toast.success('Atribuição adicionada!');
  };

  const handleRemoveAssignment = (index) => {
    setAssignments(assignments.filter((_, i) => i !== index));
  };

  const getStatusLabel = (status) => {
    const labels = {
      planned: 'Planejado',
      in_progress: 'Em Andamento',
      paused: 'Pausado',
      completed: 'Concluído'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      planned: 'text-blue-600 bg-blue-50',
      in_progress: 'text-emerald-600 bg-emerald-50',
      paused: 'text-amber-600 bg-amber-50',
      completed: 'text-slate-600 bg-slate-100'
    };
    return colors[status] || 'text-slate-600 bg-slate-100';
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-secondary font-bold uppercase tracking-tight text-slate-900">
            {isAdmin ? 'Projetos' : 'Meus Projetos'}
          </h1>
          <p className="text-slate-600 font-primary mt-1">
            {isAdmin ? 'Gerencie todos os projetos de construção' : 'Visualize seus projetos vinculados'}
          </p>
        </div>
        {isAdmin && (
          <Button 
            onClick={() => handleOpenDialog()} 
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm w-full sm:w-auto" 
            data-testid="add-project-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Projeto
          </Button>
        )}
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((project) => (
          <div
            key={project.id}
            className="bg-white border border-slate-200 rounded-sm shadow-sm p-6 hover:border-slate-300 hover:shadow-md transition-all"
            data-testid="project-card"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-sm bg-accent/10">
                <FolderKanban className="h-5 w-5 text-accent" strokeWidth={1.5} />
              </div>
              <span className={`px-2 py-1 rounded-sm text-xs font-medium ${getStatusColor(project.status)}`}>
                {getStatusLabel(project.status)}
              </span>
            </div>
            <h3 className="font-semibold text-slate-900 text-lg mb-1">{project.name}</h3>
            {project.internal_code && (
              <p className="text-xs text-slate-500 mb-2">Código: {project.internal_code}</p>
            )}
            {project.address && (
              <p className="text-sm text-slate-600 mb-3 line-clamp-2">{project.address}</p>
            )}
            <div className="flex gap-4 text-xs text-slate-600 mb-4">
              <div className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {project.clients?.length || 0} cliente(s)
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              {isAdmin ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleOpenDialog(project)}
                    className="flex-1 rounded-sm"
                    data-testid="edit-project-button"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(project.id)}
                    className="rounded-sm"
                    data-testid="delete-project-button"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenDialog(project, true)}
                  className="flex-1 rounded-sm"
                  data-testid="view-project-button"
                >
                  <Eye className="h-3 w-3 mr-1" />
                  Visualizar
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {projects.length === 0 && (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-sm">
          <FolderKanban className="h-12 w-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-slate-600">Nenhum projeto cadastrado</p>
        </div>
      )}

      {/* Project Dialog with Tabs */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary uppercase tracking-tight">
              {viewOnly ? 'Visualizar Projeto' : (editingProject ? 'Editar Projeto' : 'Novo Projeto')}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 gap-1">
              <TabsTrigger value="dados">Dados</TabsTrigger>
              <TabsTrigger value="clientes">Clientes</TabsTrigger>
              <TabsTrigger value="empreitas">Empreitas</TabsTrigger>
              <TabsTrigger value="lancamentos">Lançamentos</TabsTrigger>
            </TabsList>

            {/* TAB 1: Dados do Projeto */}
            <TabsContent value="dados" className="space-y-4 mt-4">
              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                  Nome do Projeto *
                </Label>
                <Input
                  value={projectData.name}
                  onChange={(e) => setProjectData({ ...projectData, name: e.target.value })}
                  required
                  disabled={viewOnly}
                  className={`mt-1 rounded-sm ${viewOnly ? 'bg-slate-100 text-slate-600 cursor-not-allowed' : ''}`}
                  data-testid="project-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                    Código Interno
                  </Label>
                  <Input
                    value={projectData.internal_code}
                    onChange={(e) => setProjectData({ ...projectData, internal_code: e.target.value })}
                    disabled={viewOnly}
                    className={`mt-1 rounded-sm ${viewOnly ? 'bg-slate-100 text-slate-600 cursor-not-allowed' : ''}`}
                  />
                </div>
                <div>
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                    Status *
                  </Label>
                  <Select 
                    value={projectData.status} 
                    onValueChange={(value) => setProjectData({ ...projectData, status: value })}
                    disabled={viewOnly}
                  >
                    <SelectTrigger className={`mt-1 rounded-sm ${viewOnly ? 'bg-slate-100 text-slate-600 cursor-not-allowed' : ''}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planned">Planejado</SelectItem>
                      <SelectItem value="in_progress">Em Andamento</SelectItem>
                      <SelectItem value="paused">Pausado</SelectItem>
                      <SelectItem value="completed">Concluído</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                    Data de Início
                  </Label>
                  <Input
                    type="date"
                    value={projectData.start_date}
                    onChange={(e) => setProjectData({ ...projectData, start_date: e.target.value })}
                    disabled={viewOnly}
                    className={`mt-1 rounded-sm ${viewOnly ? 'bg-slate-100 text-slate-600 cursor-not-allowed' : ''}`}
                  />
                </div>
                <div>
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                    Data de Término
                  </Label>
                  <Input
                    type="date"
                    value={projectData.end_date}
                    onChange={(e) => setProjectData({ ...projectData, end_date: e.target.value })}
                    className="mt-1 rounded-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                  Endereço
                </Label>
                <Input
                  value={projectData.address}
                  onChange={(e) => setProjectData({ ...projectData, address: e.target.value })}
                  disabled={viewOnly}
                  className={`mt-1 rounded-sm ${viewOnly ? 'bg-slate-100 text-slate-600 cursor-not-allowed' : ''}`}
                />
              </div>
              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                  Observações
                </Label>
                <Textarea
                  value={projectData.notes}
                  onChange={(e) => setProjectData({ ...projectData, notes: e.target.value })}
                  rows={3}
                  disabled={viewOnly}
                  className={`mt-1 rounded-sm ${viewOnly ? 'bg-slate-100 text-slate-600 cursor-not-allowed' : ''}`}
                />
              </div>
            </TabsContent>

            {/* TAB 2: Clientes */}
            <TabsContent value="clientes" className="space-y-4 mt-4">
              {viewOnly ? (
                // Read-only view for clients
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm text-slate-700 mb-3">Clientes Vinculados</h3>
                  {clients.length > 0 ? (
                    clients.map((client, index) => (
                      <div key={index} className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                        <p className="font-medium text-slate-900 text-sm">{client.name}</p>
                        <p className="text-xs text-slate-600">{client.email}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded-sm">
                      Nenhum cliente vinculado
                    </p>
                  )}
                </div>
              ) : (
              <>
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                <h3 className="font-semibold text-sm text-slate-700 mb-3">Selecionar Clientes Cadastrados</h3>
                <p className="text-xs text-slate-500 mb-3">
                  Marque os clientes que devem ser vinculados a este projeto. Clientes são criados na seção "Usuários" com o tipo "Cliente".
                </p>
                
                {clientUsers.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {clientUsers.map(clientUser => (
                      <div 
                        key={clientUser.id} 
                        className={`border rounded-sm p-3 cursor-pointer transition-all ${
                          isClientSelected(clientUser.id) 
                            ? 'border-accent bg-accent/5' 
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                        onClick={() => toggleClientSelection(clientUser)}
                        data-testid={`client-option-${clientUser.id}`}
                      >
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            checked={isClientSelected(clientUser.id)}
                            onCheckedChange={() => toggleClientSelection(clientUser)}
                            className="pointer-events-none"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-slate-900 text-sm">{clientUser.name}</p>
                            <p className="text-xs text-slate-600">{clientUser.email}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 border border-dashed border-slate-300 rounded-sm">
                    <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Nenhum cliente cadastrado</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Crie usuários do tipo "Cliente" na seção Usuários
                    </p>
                  </div>
                )}
              </div>

              {/* Selected clients summary */}
              <div>
                <h4 className="font-semibold text-sm text-slate-700 mb-2">
                  Clientes Selecionados ({clients.length})
                </h4>
                <div className="space-y-2">
                  {clients.map((client, index) => (
                    <div key={index} className="bg-white border border-slate-200 rounded-sm p-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{client.name}</p>
                        <p className="text-xs text-slate-600">{client.email}</p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleClientSelection({ id: client.user_id, name: client.name, email: client.email })}
                        data-testid={`remove-client-${client.user_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  ))}
                  {clients.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded-sm">
                      Nenhum cliente selecionado
                    </p>
                  )}
                </div>
              </div>
              </>
              )}
            </TabsContent>


            {/* TAB: Empreitas */}
            <TabsContent value="empreitas" className="space-y-4 mt-4">
              {editingProject?.id || viewOnly ? (
                <EmpreitasManager projectId={editingProject?.id} />
              ) : (
                <div className="text-center py-8 bg-slate-50 rounded-sm border border-dashed border-slate-300">
                  <p className="text-sm text-slate-500">Salve o projeto primeiro para adicionar empreitas</p>
                </div>
              )}
            </TabsContent>

            {/* TAB 4: Lançamentos */}
            <TabsContent value="lancamentos" className="space-y-4 mt-4">
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-slate-700">Histórico de Lançamentos</h3>
                {(() => {
                  // Filter launches: clients only see client-type launches
                  const visibleLaunches = !isAdmin 
                    ? projectLaunches.filter(l => l.launch_type !== 'direct')
                    : projectLaunches;
                  
                  return visibleLaunches.length > 0 ? (
                    visibleLaunches.map((launch) => {
                      const provider = providers.find(p => p.id === launch.provider_id);
                      return (
                        <div key={launch.id} className={`bg-white border rounded-sm p-4 ${
                          launch.launch_type === 'direct' ? 'border-purple-200' : 'border-slate-200'
                        }`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-slate-900">{launch.service_description}</p>
                                {launch.launch_type === 'direct' && (
                                  <span className="text-xs px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded-sm">
                                    Direto
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {provider?.name || 'Prestador não identificado'} • {launch.service_code}
                              </p>
                            </div>
                            {launch.status === 'paid' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium text-emerald-600 bg-emerald-50">
                                <CheckCircle2 className="h-3 w-3" />
                                Pago
                              </span>
                            ) : launch.status === 'submitted_receipt' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium text-blue-600 bg-blue-50">
                                <Clock className="h-3 w-3" />
                                Comprovante Enviado
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium text-amber-600 bg-amber-50">
                                <Clock className="h-3 w-3" />
                                Pendente
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
                            <p className="text-xs text-slate-500">
                              {new Date(launch.launch_date).toLocaleDateString('pt-BR')}
                            </p>
                            <p className="font-semibold text-slate-900">
                              R$ {launch.amount?.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 bg-slate-50 rounded-sm">
                      <Receipt className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Nenhum lançamento encontrado</p>
                    </div>
                  );
                })()}
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex gap-2 justify-end mt-4 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
              {viewOnly ? 'Fechar' : 'Cancelar'}
            </Button>
            {!viewOnly && (
              <Button onClick={handleSubmit} className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm">
                {editingProject ? 'Atualizar Projeto' : 'Criar Projeto'}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;
